/**
 * PromoCarousel — HOH Vibes Studio app landing page hero.
 *
 * Requirements:
 * - Auto-advance every 5s with smooth fade + slide transition
 * - Swipe left/right on touch devices
 * - Arrow prev/next on desktop
 * - Dots for direct navigation
 * - Pause autoplay on user interaction (hover / focus / touch)
 * - Preserves image aspect ratio (no forced crop / stretch)
 * - Each slide is clickable to its own destination and shows a permanent
 *   "Download the App" CTA overlay
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";

const INTERVAL_MS = 5000;

export default function PromoCarousel({ slides = [] }) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStart = useRef(null);
  const touchDelta = useRef(0);

  const count = slides.length;
  const clampedIdx = count > 0 ? ((idx % count) + count) % count : 0;

  const go = useCallback((n) => {
    if (!count) return;
    setIdx((prev) => ((prev + n) % count + count) % count);
  }, [count]);
  const jumpTo = (i) => { setIdx(i); setPaused(true); setTimeout(() => setPaused(false), INTERVAL_MS * 2); };

  // Autoplay
  useEffect(() => {
    if (paused || count <= 1) return;
    const t = window.setInterval(() => setIdx((p) => (p + 1) % count), INTERVAL_MS);
    return () => window.clearInterval(t);
  }, [paused, count]);

  // Keyboard arrows
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go]);

  const onTouchStart = (e) => {
    setPaused(true);
    touchStart.current = e.touches[0].clientX;
    touchDelta.current = 0;
  };
  const onTouchMove = (e) => {
    if (touchStart.current == null) return;
    touchDelta.current = e.touches[0].clientX - touchStart.current;
  };
  const onTouchEnd = () => {
    const d = touchDelta.current;
    if (Math.abs(d) > 40) go(d < 0 ? 1 : -1);
    touchStart.current = null;
    touchDelta.current = 0;
    setTimeout(() => setPaused(false), INTERVAL_MS * 2);
  };

  if (!count) {
    return (
      <div className="w-full aspect-[16/10] rounded-2xl bg-neutral-900/50 border border-white/5 grid place-items-center text-neutral-500 text-xs tracking-widest uppercase">
        No promotional slides yet
      </div>
    );
  }

  const current = slides[clampedIdx];

  return (
    <div
      data-testid="promo-carousel"
      className="relative w-full select-none"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Frame preserves aspect ratio — image is contained, never cropped */}
      <div className="relative w-full overflow-hidden rounded-2xl bg-gradient-to-br from-[#1a0a0f] via-black to-[#0a0d0a] ring-1 ring-[#D4AF37]/20 shadow-[0_25px_80px_-20px_rgba(0,0,0,0.9)]">
        <div className="relative w-full" style={{ aspectRatio: "3 / 4", maxHeight: "min(78vh, 900px)" }}>
          {slides.map((s, i) => (
            <a
              key={s.id || i}
              href={s.link_url || "https://link-my.app/hohvibes-studio"}
              target="_blank"
              rel="noreferrer noopener"
              data-testid={`promo-slide-${i}`}
              className={`absolute inset-0 transition-opacity duration-700 ease-out ${i === clampedIdx ? "opacity-100 z-10" : "opacity-0 z-0 pointer-events-none"}`}
              aria-hidden={i === clampedIdx ? "false" : "true"}
              tabIndex={i === clampedIdx ? 0 : -1}
            >
              <img
                src={s.image_url}
                alt={s.alt || s.label || "HOH Vibes Studio promotion"}
                loading={i === 0 ? "eager" : "lazy"}
                className="w-full h-full object-contain"
                draggable={false}
              />
            </a>
          ))}
        </div>

        {/* Permanent CTA overlay */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 p-4 sm:p-6">
          <div className="flex items-end justify-between gap-4">
            <div className="pointer-events-auto max-w-[75%]">
              {current.label && (
                <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37] font-semibold drop-shadow-lg">
                  {current.label}
                </div>
              )}
            </div>
            <a
              data-testid="promo-cta"
              href={current.link_url || "https://link-my.app/hohvibes-studio"}
              target="_blank"
              rel="noreferrer noopener"
              className="pointer-events-auto inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#e6c04a] text-black px-4 py-2.5 sm:px-5 sm:py-3 rounded-full text-[10px] sm:text-[11px] font-semibold tracking-[0.22em] uppercase shadow-lg transition-colors"
            >
              Download the App <ArrowRight size={13} />
            </a>
          </div>
        </div>

        {/* Prev / Next — desktop */}
        {count > 1 && (
          <>
            <button
              data-testid="promo-prev"
              type="button"
              aria-label="Previous slide"
              onClick={() => { go(-1); setPaused(true); setTimeout(() => setPaused(false), INTERVAL_MS * 2); }}
              className="hidden md:grid place-items-center absolute z-30 left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur border border-white/10 text-white transition-colors"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              data-testid="promo-next"
              type="button"
              aria-label="Next slide"
              onClick={() => { go(1); setPaused(true); setTimeout(() => setPaused(false), INTERVAL_MS * 2); }}
              className="hidden md:grid place-items-center absolute z-30 right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur border border-white/10 text-white transition-colors"
            >
              <ChevronRight size={20} />
            </button>
          </>
        )}
      </div>

      {/* Dots */}
      {count > 1 && (
        <div className="mt-5 flex justify-center gap-2" data-testid="promo-dots">
          {slides.map((s, i) => (
            <button
              key={s.id || i}
              type="button"
              aria-label={`Go to slide ${i + 1}`}
              data-testid={`promo-dot-${i}`}
              onClick={() => jumpTo(i)}
              className={`h-1.5 rounded-full transition-all duration-300 ${i === clampedIdx ? "bg-[#D4AF37] w-8" : "bg-neutral-600 hover:bg-neutral-400 w-2"}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
